import { Component, inject, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PayoutsStoreService } from 'src/app/services/stores/payouts.store.service';

export interface PayoutRequestDialogData {
  userId: string;
  userEmail: string;
  currentBalance: number;
}

@Component({
  selector: 'app-payout-request-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './payout-request-dialog.component.html',
  styleUrl: './payout-request-dialog.component.scss',
})
export class PayoutRequestDialogComponent {
  private _dialogRef = inject(MatDialogRef<PayoutRequestDialogComponent>);
  private _data: PayoutRequestDialogData = inject(MAT_DIALOG_DATA);
  private _fb = inject(FormBuilder);
  private _payoutsStoreService = inject(PayoutsStoreService);

  protected payoutForm: FormGroup;
  protected isSubmitting: WritableSignal<boolean> = signal(false);
  protected submitError: WritableSignal<string | null> = signal(null);
  protected submitSuccess: WritableSignal<boolean> = signal(false);

  constructor() {
    this.payoutForm = this._fb.group({
      paymentName: ['', [Validators.required, Validators.minLength(2)]],
      paymentEmail: ['', [Validators.required, Validators.email]],
    });
  }

  protected get currentBalance(): number {
    return this._data.currentBalance;
  }

  protected async onSubmit(): Promise<void> {
    if (this.payoutForm.invalid || this.isSubmitting()) {
      return;
    }

    // Validate balance is positive
    if (this._data.currentBalance <= 0) {
      this.submitError.set(
        'Insufficient balance. You need a positive balance to request a payout.',
      );
      return;
    }

    this.isSubmitting.set(true);
    this.submitError.set(null);

    try {
      const formValue = this.payoutForm.value;
      await this._payoutsStoreService.createPayout(
        this._data.userId,
        this._data.userEmail,
        formValue.paymentName,
        formValue.paymentEmail,
        this._data.currentBalance,
      );

      this.submitSuccess.set(true);

      // Close dialog after showing success message
      setTimeout(() => {
        this._dialogRef.close(true);
      }, 5000);
    } catch (error) {
      console.error('Error creating payout:', error);
      this.submitError.set(
        'Failed to submit payout request. Please try again.',
      );
      this.isSubmitting.set(false);
    }
  }

  protected onCancel(): void {
    this._dialogRef.close(false);
  }
}
